import { useEffect, useMemo, useState } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { AlertTriangle, Ban, Check, Loader2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import {
  producaoApi,
  errorMessage,
  type AvailabilityResult,
  type Conflict,
} from "./api"
import { useProducao } from "./context"
import { MaterialStatePill } from "./components"
import { useMaterial } from "./material"
import { addDays, fmtDay, fmtEur, initialsOf, isSunday, kindText, todayIso, workDays } from "./lib"

export const selectClass =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

/** Próximo dia de trabalho a partir de amanhã (o domingo salta-se). */
function nextWorkDay(): string {
  let d = addDays(todayIso(), 1)
  while (isSunday(d)) d = addDays(d, 1)
  return d
}

/**
 * G3 · Atribuir obra — painel lateral por cima de qualquer ecrã. Os conflitos
 * aparecem antes de gravar (disponibilidade vinda do servidor, com a mesma
 * lógica que o `assign` valida). O material em falta é um aviso que não
 * bloqueia.
 */
export function AssignPanel() {
  const { assign, closeAssign } = useProducao()
  return (
    <DialogPrimitive.Root open={!!assign} onOpenChange={(o) => !o && closeAssign()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <DialogPrimitive.Content
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[480px] flex-col border-l bg-background shadow-xl outline-none border-border"
          aria-describedby={undefined}
        >
          {assign && <AssignForm key={assign.job.id} />}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

function AssignForm() {
  const { shell, board, assign, closeAssign, upsertJob } = useProducao()
  const job = assign!.job
  const vans = (board?.vans ?? []).filter((v) => v.active)
  // As pessoas do armazém (0.45.0) não têm obras nem carrinha.
  const technicians = (board?.technicians ?? []).filter((t) => t.active && t.kind !== "armazem")
  const threshold = board?.config.overload_threshold ?? 4

  const [date, setDate] = useState(assign?.preset?.date ?? job.scheduled_date ?? nextWorkDay())
  const [duration, setDuration] = useState(job.duration_days || 1)
  const [vanId, setVanId] = useState<string | null>(assign?.preset?.van_id ?? job.van_id)
  const [leadId, setLeadId] = useState<string | null>(
    job.lead_technician_id ?? vans.find((v) => v.id === (assign?.preset?.van_id ?? job.van_id))?.technician_id ?? null,
  )
  const [techIds, setTechIds] = useState<string[]>(
    job.technician_ids.filter((id) => id !== job.lead_technician_id),
  )
  const [note, setNote] = useState(job.note ?? "")
  const [changeLead, setChangeLead] = useState(false)
  const [avail, setAvail] = useState<AvailabilityResult | null>(null)
  const [saving, setSaving] = useState(false)
  const [conflicts, setConflicts] = useState<Conflict[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // M4: o contrato aceitou a atribuição mas a obra não se gravou.
  const [contractAhead, setContractAhead] = useState(false)

  const material = useMaterial(job.id, job.odoo_sale_order_id != null)
  const days = useMemo(() => (date ? workDays(date, duration) : []), [date, duration])

  useEffect(() => {
    if (!shell || !date) return
    let cancelled = false
    producaoApi
      .availability(shell, { date, duration, van_id: vanId, job_id: job.id })
      .then((r) => !cancelled && setAvail(r))
      .catch(() => !cancelled && setAvail(null))
    return () => {
      cancelled = true
    }
  }, [shell, date, duration, vanId, job.id])

  // Qualquer mudança invalida os conflitos devolvidos pelo servidor.
  useEffect(() => setConflicts(null), [date, duration, vanId, leadId, techIds])

  const pickVan = (id: string) => {
    setVanId(id)
    const van = vans.find((v) => v.id === id)
    // Escolher a carrinha pré-preenche o chefe fixo dela.
    if (van?.technician_id) {
      setLeadId(van.technician_id)
      setTechIds((ids) => ids.filter((x) => x !== van.technician_id))
    }
    setChangeLead(false)
  }

  const toggleTech = (id: string) =>
    setTechIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]))

  const lead = technicians.find((t) => t.id === leadId)
  const leadAvail = leadId ? avail?.availability[leadId] : undefined
  const maxLoad = Math.max(0, ...Object.values(avail?.van_load ?? {}))
  const overloaded = vanId && maxLoad + 1 >= threshold
  const hasOnlyWarnings = conflicts && conflicts.length > 0 && conflicts.every((c) => c.severity === "aviso")

  const submit = async (force: boolean) => {
    if (!shell) return
    setSaving(true)
    setError(null)
    try {
      const r = await producaoApi.assign(shell, job.id, {
        scheduled_date: date || null,
        duration_days: duration,
        van_id: vanId,
        lead_technician_id: leadId,
        technician_ids: techIds.filter((id) => id !== leadId),
        note: note.trim() || null,
        force,
        version: job.version,
      })
      if (!r.saved) {
        setConflicts(r.conflicts)
        // Modo `host`: o contrato recusou (parado, sem equipa, …) — nada ficou gravado.
        if (r.contract_error) setError(`Contrato do Koortex: ${r.contract_error}`)
        if (r.contract_ahead) setContractAhead(true)
        return
      }
      upsertJob(r.job)
      const trelloOk = r.job.trello_sync_status === "ok" || r.job.trello_sync_status == null
      shell.bridge.toast({
        variant: trelloOk ? "success" : "info",
        title: trelloOk ? "Atribuída · Trello atualizado" : "Atribuída · Trello por sincronizar",
        description: trelloOk ? undefined : "A atribuição está gravada. O Trello volta a tentar-se sozinho.",
      })
      closeAssign()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const syncFromContract = async () => {
    if (!shell) return
    setSaving(true)
    try {
      const updated = await producaoApi.syncFromContract(shell, job.id)
      upsertJob(updated)
      shell.bridge.toast({ variant: "success", title: "Obra sincronizada com o contrato" })
      closeAssign()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const missingFields = [!date && "data", !vanId && "carrinha", !leadId && "chefe"].filter(Boolean)

  return (
    <>
      <div className="flex items-start justify-between gap-3 border-b px-5 py-4 border-border">
        <div className="min-w-0">
          <DialogPrimitive.Title className="text-base font-semibold">
            {job.status === "por_atribuir" ? "Atribuir obra" : "Reatribuir obra"}
          </DialogPrimitive.Title>
          <p className="mt-0.5 truncate text-sm font-medium">{job.client_name}</p>
          <p className="truncate text-xs text-muted-foreground">
            {[kindText(job), [job.address, job.city].filter(Boolean).join(", "), fmtEur(job.value_eur)]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <DialogPrimitive.Close className="rounded-sm p-1 opacity-70 hover:opacity-100" aria-label="Fechar">
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
        {/* 1 · Data e duração */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">1 · Data</h3>
          <div className="flex gap-3">
            <div className="flex-1">
              <Label htmlFor="as-date" className="sr-only">Data de início</Label>
              <Input id="as-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="w-36">
              <Label htmlFor="as-dur" className="sr-only">Duração</Label>
              <select
                id="as-dur"
                className={selectClass}
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? "dia" : "dias"}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {date && (
            <p className="text-xs text-muted-foreground">
              {isSunday(date) && "Domingo não conta — começa na segunda. "}
              {days.length > 1 ? `De ${fmtDay(days[0]!)} a ${fmtDay(days[days.length - 1]!)} (seg–sáb)` : fmtDay(days[0]!)}
            </p>
          )}
        </section>

        {/* 2 · Carrinha */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">2 · Carrinha</h3>
          {vans.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem carrinhas. Cria-as em Equipas.</p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {vans.map((v) => {
                const chefe = technicians.find((t) => t.id === v.technician_id)
                const on = v.id === vanId
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => pickVan(v.id)}
                    className={cn(
                      "flex flex-col items-start gap-1 rounded-lg border-2 p-3 text-left transition-colors border-border",
                      on ? "border-foreground bg-muted/60" : "border-border hover:bg-muted/40",
                    )}
                  >
                    <span className="flex items-center gap-1.5 text-sm font-semibold">
                      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: v.color }} />
                      {v.name}
                    </span>
                    <span className="text-xs text-muted-foreground">{chefe?.name ?? "Sem chefe fixo"}</span>
                    {on && avail && (
                      <span className="text-xs text-muted-foreground">
                        {maxLoad} {maxLoad === 1 ? "obra" : "obras"} no dia
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
          {overloaded && (
            <Warn>
              Seria a {maxLoad + 1}.ª obra da carrinha no mesmo dia (a média é 2 a 3). Não bloqueia.
            </Warn>
          )}
        </section>

        {/* 3 · Chefe */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">3 · Chefe de equipa</h3>
          {!lead && !changeLead ? (
            <p className="text-sm text-muted-foreground">
              Escolhe a carrinha — o chefe fixo dela entra automaticamente.{" "}
              <button type="button" className="text-accent underline" onClick={() => setChangeLead(true)}>
                Escolher à mão
              </button>
            </p>
          ) : changeLead ? (
            <select
              className={selectClass}
              value={leadId ?? ""}
              onChange={(e) => {
                const id = e.target.value || null
                setLeadId(id)
                setTechIds((ids) => ids.filter((x) => x !== id))
                setChangeLead(false)
              }}
              aria-label="Chefe de equipa"
            >
              <option value="">—</option>
              {[...technicians]
                .sort((a, b) => Number(b.is_lead) - Number(a.is_lead) || a.name.localeCompare(b.name))
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.is_lead ? " (chefe)" : ""}
                    {avail?.availability[t.id]?.status === "ausente" ? " — ausente" : ""}
                  </option>
                ))}
            </select>
          ) : (
            <div className="flex items-center justify-between rounded-lg border p-3 border-border">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground text-xs font-semibold text-background">
                  {initialsOf(lead!)}
                </span>
                <div>
                  <div className="text-sm font-medium">{lead!.name}</div>
                  <AvailText a={leadAvail} />
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setChangeLead(true)}>
                Trocar
              </Button>
            </div>
          )}
        </section>

        {/* 4 · Técnicos */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold">4 · Técnicos</h3>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {technicians
              .filter((t) => t.id !== leadId)
              .map((t) => {
                const a = avail?.availability[t.id]
                const absent = a?.status === "ausente"
                const on = techIds.includes(t.id)
                return (
                  <li key={t.id}>
                    <label
                      className={cn(
                        "flex cursor-pointer items-center gap-3 px-3 py-2",
                        absent && "cursor-not-allowed opacity-60",
                      )}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={on}
                        disabled={absent && !on}
                        onChange={() => toggleTech(t.id)}
                      />
                      <span className="flex-1 text-sm">{t.name}</span>
                      <AvailText a={a} />
                    </label>
                  </li>
                )
              })}
            {technicians.filter((t) => t.id !== leadId).length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">Sem outros técnicos ativos.</li>
            )}
          </ul>
        </section>

        {/* Material — aviso, não bloqueia */}
        <section className="space-y-2 rounded-lg bg-muted/50 p-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Material desta obra</h3>
            {job.odoo_sale_order_id != null ? (
              <MaterialStatePill state={material} />
            ) : (
              <span className="text-xs text-muted-foreground">Sem ordem no Odoo</span>
            )}
          </div>
          {material?.status === "faltam" && (
            <>
              <ul className="space-y-0.5 text-xs">
                {material.lines
                  .filter((l) => l.missing > 0)
                  .map((l, i) => (
                    <li key={i}>
                      {l.missing} {l.uom ?? ""} × {l.product}
                    </li>
                  ))}
              </ul>
              <p className="text-xs text-muted-foreground">Pode atribuir na mesma — fica marcada no quadro.</p>
            </>
          )}
        </section>

        {/* 5 · Recado */}
        <section className="space-y-2">
          <Label htmlFor="as-note" className="text-sm font-semibold text-foreground">
            5 · Recado para a equipa
          </Label>
          <textarea
            id="as-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Aparece na app como «Recado do escritório»"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </section>

        {conflicts && conflicts.length > 0 && (
          <section className="space-y-2" aria-live="polite">
            {conflicts.map((c, i) =>
              c.severity === "erro" ? (
                <div key={i} className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
                  <Ban className="mt-0.5 h-4 w-4 shrink-0" />
                  {c.message}
                </div>
              ) : (
                <Warn key={i}>{c.message}</Warn>
              ),
            )}
          </section>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {contractAhead && (
          <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" aria-live="polite">
            <p>Contrato atualizado; a obra ficou por atualizar.</p>
            <Button size="sm" variant="outline" onClick={syncFromContract} disabled={saving}>
              Sincronizar
            </Button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t px-5 py-3 border-border">
        <p className="text-xs text-muted-foreground">
          {missingFields.length > 0
            ? `Falta: ${missingFields.join(", ")}.`
            : hasOnlyWarnings
              ? "Há avisos. Podes atribuir mesmo assim."
              : "Grava no Koortex e atualiza o Trello."}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={closeAssign}>
            Cancelar
          </Button>
          <Button onClick={() => submit(!!hasOnlyWarnings)} disabled={saving || missingFields.length > 0}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {hasOnlyWarnings ? "Atribuir mesmo assim" : "Atribuir"}
          </Button>
        </div>
      </div>
    </>
  )
}

function AvailText({ a }: { a: AvailabilityResult["availability"][string] | undefined }) {
  if (!a) return null
  if (a.status === "ausente")
    return <span className="text-xs font-medium text-red-700">Ausente · {a.reason}</span>
  if (a.status === "ocupado")
    return <span className="text-xs font-medium text-amber-700">Já escalado: {a.reason}</span>
  return <span className="text-xs text-green-700">Disponível</span>
}

function Warn({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </div>
  )
}
